import { blogHomePath, blogLocaleFor } from '~/constants/blog'
import { DEFAULT_LOCALE, isAppLocale, LOCALE_COOKIE, localeFromLanguages, localeFromPath, parseAcceptLanguage, type AppLocale } from '~/constants/appLocale'

/**
 * Whether a route has a URL per language (/pricing, /cs/pricing). The marketing
 * pages do; the app and the blog opt out with `defineI18nRoute(false)`, and
 * @nuxtjs/i18n then leaves their route name without the "___<locale>" suffix.
 */
export function isLocalizedRoute(route: { name?: unknown }): boolean {
  return typeof route.name === 'string' && /___[a-z]+$/.test(route.name)
}

/**
 * The language the site is shown in for this visitor (constants/appLocale.ts).
 *
 * On a marketing page the URL decides, so a link or a search result always
 * shows the language it promised. Everywhere else (the app) the remembered
 * language does: the switcher's choice or the language of the last marketing
 * page read, kept in a cookie for a year, and the browser's languages before
 * there is one.
 */
export function useAppLocale() {
  const nuxtApp = useNuxtApp()
  const router = useRouter()
  const localize = useLocalizedPath()
  // What @nuxtjs/i18n hands the browser, so it hydrates in the language the server rendered.
  const resolvedLocale = useState<string>('i18n:resolved-locale')
  const cookie = useCookie<string | null>(LOCALE_COOKIE, {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
    path: '/',
  })

  function detect(): AppLocale {
    if (import.meta.server) {
      return localeFromLanguages(parseAcceptLanguage(useRequestHeaders(['accept-language'])['accept-language']))
    }
    return localeFromLanguages(navigator.languages?.length ? navigator.languages : [navigator.language])
  }

  /** The remembered language, if there is one yet. */
  function remembered(): AppLocale | null {
    return isAppLocale(cookie.value) ? cookie.value : null
  }

  function remember(locale: AppLocale) {
    if (cookie.value !== locale) cookie.value = locale
  }

  /** The visitor's app language, remembered once known. */
  function preferred(): AppLocale {
    const known = remembered()
    if (known) return known
    const detected = detect()
    remember(detected)
    return detected
  }

  /**
   * Shows the current page in a language without changing its URL, for pages
   * that have a single URL. i18n.setLocale() would instead navigate to the
   * previous page's translation when called from a route middleware.
   */
  async function apply(locale: AppLocale) {
    const i18n = nuxtApp.$i18n
    if (i18n.locale.value === locale) return
    await i18n.loadLocaleMessages(locale)
    i18n.locale.value = locale
    resolvedLocale.value = locale
  }

  /** Switches the language: to the page's translation, or in place where the page has one URL. */
  async function choose(locale: AppLocale) {
    remember(locale)
    const route = router.currentRoute.value
    // A blog page has no translation; another language with its own blog
    // goes to that blog's home, any other stays where it is.
    const blog = blogLocaleFor(locale)
    if (route.meta.blogLocale && route.meta.blogLocale !== blog) {
      return nuxtApp.runWithContext(() => navigateTo(blogHomePath(blog)))
    }
    if (isLocalizedRoute(route)) {
      if (route.meta.blogLocale) return
      return nuxtApp.runWithContext(() => navigateTo(localize(route.fullPath, locale)))
    }
    if (!route.meta.englishOnly) await apply(locale)
  }

  /** The language the switcher marks: the page's own on a marketing page, the remembered one elsewhere. */
  const current = computed<AppLocale>(() => {
    const route = router.currentRoute.value
    return isLocalizedRoute(route) ? localeFromPath(route.path) : (remembered() ?? DEFAULT_LOCALE)
  })

  return { detect, remembered, remember, preferred, apply, choose, current }
}
